import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CapacitorHttp } from "@capacitor/core";
import { auth } from "../firebase/firebase";
import Papa from "papaparse";
import topicsCSV from "../data/topics.csv?url";

const badgeEmojis = {
  "Brave Beginner": "🦨",
  "Vocabulary Explorer": "🔍",
  "Confident Speaker": "🎤",
  "Story Star": "🌟",
  "English Hero": "🧘",
  "Grammar Guru": "📚",
  "Conversation Champ": "💬",
  "Listening Legend": "🎧",
  "Presentation Pro": "🗣️",
  "Quick Responder": "⚡",
  "Imagination Master": "🎨",
  "Vocabulary Wizard": "🦤",
  "Super Speaker 🚀": "🚀",
  "Tone Master 🎤": "🎼",
  "Quiz Genius 🧐": "🧐",
  "Storyteller Supreme 📖": "📖",
  "Language Champion 🏆": "🏆",
  "GiGi Grandmaster 🌟": "👑"
};

const Home = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [babyName, setBabyName] = useState(localStorage.getItem("child_name") || "");
  const [birthYear, setBirthYear] = useState("");
  const [userStats, setUserStats] = useState(null);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    fetch(topicsCSV)
      .then(res => res.text())
      .then(text => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: result => setTopics(result.data)
        });
      });
  }, []);

  const START_DATE = new Date("2025-03-03");
  const getTodayIndex = () => {
    const today = new Date();
    const diffTime = today - START_DATE;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays % topics.length;
  };

  const todayTopic = topics.length > 0 ? topics[getTodayIndex()] : null;

  useEffect(() => {
    const fetchUserData = async () => {
      const childId = localStorage.getItem("child_id");
      if (!childId || !auth.currentUser) {
        setLoading(false);
        return;
      }
      try {
        const idToken = await auth.currentUser.getIdToken();

        const statsResp = await CapacitorHttp.get({
          url: `https://firestore.googleapis.com/v1/projects/gigitherobot-4550d/databases/(default)/documents/user_stats/${childId}`,
          headers: { Authorization: `Bearer ${idToken}` }
        });

        const statsFields = statsResp.data.fields;
        if (statsFields) {
          setUserStats(statsFields);
        }
      } catch (err) {
        console.error("Lỗi lấy user stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const onGuideClick = useCallback(() => navigate("/6-introduction"), [navigate]);
  const onHistoryClick = useCallback(() => navigate("/41-history-calendar"), [navigate]);
  const onWifiClick = useCallback(() => {
    const child_id = localStorage.getItem("child_id");
    const child_name = localStorage.getItem("child_name");
    window.open(`http://192.168.4.1:8000/?child_id=${child_id}&child_name=${child_name}`, "_blank");
  }, []);

  if (loading) return <div className="p-10 text-center">Đang tải...</div>;

  return (
    <div className="font-text-regular-lowercase w-full min-h-screen bg-light-primary-key-background flex flex-col items-center px-6 py-10 gap-8">
      <div className="w-full max-w-md flex flex-col items-center gap-4">
        <div className="rounded-full bg-pink-100 p-4">
          <img src="chatgpt-image-13-29-24-5-thg-4-2025-1@2x.png" alt="Robot GiGi" className="w-24 h-24 object-contain" />
        </div>

        <div className="text-lg font-medium text-center">
          Máy của <span className="text-pink-500">{babyName}</span>
        </div>

        <div className="flex flex-col items-center gap-3 mt-4 text-sm font-semibold w-full px-4">
          <div onClick={onGuideClick} className="w-full bg-[#FFF2EA] rounded-full h-[52px] flex items-center justify-center text-[#FF8C92] cursor-pointer">
            Hướng dẫn sử dụng
          </div>
          <div onClick={onHistoryClick} className="w-full bg-[#FFF2EA] rounded-full h-[52px] flex items-center justify-center text-[#FF8C92] cursor-pointer">
            Xem lịch sử trò chuyện
          </div>
          <div onClick={onWifiClick} className="w-full bg-[#FFF2EA] rounded-full h-[52px] flex items-center justify-center text-[#FF8C92] cursor-pointer">
            Kết nối Wi-Fi
          </div>
        </div>

        {userStats && (
          <div className="w-full bg-white rounded-xl shadow-md p-4 mt-4">
            <p className="text-lg font-bold text-pink-500">📊 Thành tích</p>
            <p>⭐ Level: {userStats.level?.integerValue || 1}</p>
            <p>✨ XP: {userStats.xp?.integerValue || 0}</p>
            <div className="mt-2">
              <p className="font-semibold text-pink-400 mb-1">🏅 Huy hiệu:</p>
              <div className="flex flex-wrap gap-2">
                {(userStats.badges?.arrayValue?.values || []).map((badge, i) => {
                  const label = badge?.stringValue || badge?.mapValue?.fields?.name?.stringValue || "🏅";
                  return (
                    <span key={i} className="bg-pink-100 text-pink-700 rounded-full px-3 py-1 text-sm flex items-center gap-1">
                      {badgeEmojis[label] || "🏅"} {label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {todayTopic && (
          <div className="mt-6 w-full bg-white rounded-xl shadow p-4">
            <h3 className="text-lg font-bold text-pink-500 mb-2">
              🧠 Bài học hôm nay: {todayTopic.Day} - {todayTopic.Topic}
            </h3>
            <p><strong>Từ vựng:</strong> {todayTopic.Vocabulary}</p>
            <p><strong>Câu hỏi gợi ý:</strong> {todayTopic["Suggested Questions"]}</p>
            <p><strong>Mẫu ngữ pháp:</strong> {todayTopic["Grammar Samples"]}</p>
            <p><strong>Hoạt động:</strong> {todayTopic.Activity}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;

